import React, { FC, useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import { Loader } from '@eika/loader';

import { useConfig } from '../../default-config';
import { EiendomType, EiendomTypeWeb, SoeknadstypeWeb, ValgtWeb } from '../../generated-types';
import { useEiendomverdi, useUpdateEiendomsverdi } from '../../hooks/useEiendom';
import useEngasjement from '../../hooks/useEngasjement';
import { useTranslation } from '../../translations';
import AnnenEiendom, { ANNEN_EIENDOM_ID, AnnenEiendomFormData } from '../eiendom/annenEiendom';
import ETakstFormData from '../eiendom/eTakstFormData';
import ETakstSoek from '../eiendom/eTakstSoek';
import ETakstValg from '../eiendom/eTakstValg';
import EiendomList, { ValgtEiendomFormData } from '../eiendom/eiendomList';
import Verdivurdering, { OenskerVerdivurdering } from '../eiendom/verdivurdering';
import PageCard from '../page/pageCard';
import PageFooter from '../page/pageFooter';
import BoligPant, { BoligPantFormData } from './boligPant';
import BoligSalgInfoBox from './boligSalgInfoBox';
import BoligSalgValg, { BoligSalgValgFormData } from './boligSalgValg';

export type BoligSalgFormData = BoligSalgValgFormData &
    ETakstFormData &
    BoligPantFormData &
    ValgtEiendomFormData &
    AnnenEiendomFormData &
    OenskerVerdivurdering;

interface Props {
    soeknadstype: SoeknadstypeWeb;
    navigateTo: string;
    navigateFrom: string;
}

const BoligSalg: FC<Props> = ({ soeknadstype, navigateTo, navigateFrom }) => {
    const navigate = useNavigate();
    const { boligSalg } = useTranslation();
    const { featureToggles } = useConfig();
    const {
        data: eiendomsverdi,
        isLoading: isLoadingEiendomsverdi,
        isFetched: isFetchedEiendomsverdi,
    } = useEiendomverdi(soeknadstype, EiendomTypeWeb.SELLING);
    const { data: engasjementer, isLoading: isLoadingEngasjementer } = useEngasjement(eiendomsverdi?.soeknadId || '');
    const { mutate: updateEiendomverdi, isPending: isUpdating } = useUpdateEiendomsverdi(eiendomsverdi, () => {
        if (eiendomsverdi) {
            navigate(navigateTo);
        }
    });

    const methods = useForm<BoligSalgFormData>({
        defaultValues: {
            harBolig: eiendomsverdi?.harBolig || ValgtWeb.IKKE_VALGT,
            harETakst: eiendomsverdi?.harEtakst || ValgtWeb.IKKE_VALGT,
            harBoligPant: eiendomsverdi?.harPantIBolig || ValgtWeb.IKKE_VALGT,
            eTakst: eiendomsverdi?.eiendom?.etakstWeb || undefined,
            eiendom: eiendomsverdi?.eiendom || undefined,
            adresse:
                eiendomsverdi?.eiendom?.eiendomId === ANNEN_EIENDOM_ID ? eiendomsverdi?.eiendom?.adresse : undefined,
            postnummer:
                eiendomsverdi?.eiendom?.eiendomId === ANNEN_EIENDOM_ID ? eiendomsverdi?.eiendom?.postnummer : undefined,
            poststed:
                eiendomsverdi?.eiendom?.eiendomId === ANNEN_EIENDOM_ID ? eiendomsverdi?.eiendom?.poststed : undefined,
            restSaldo: eiendomsverdi?.eksisterendeLaanebeloep || undefined,
            oenskerVerdivurdering: eiendomsverdi?.eiendom?.oenskerVerdivurdering || ValgtWeb.IKKE_VALGT,
            samtykke: false,
        },
    });
    const { handleSubmit, watch, reset } = methods;
    const watchAll = watch();
    const eiendomId = watchAll.eiendom?.eiendomId;
    const engasjement =
        engasjementer && eiendomId ? engasjementer.find((e) => e.eiendomsId.includes(eiendomId)) : undefined;

    if (eiendomsverdi?.eiendom?.eiendomId.length === 0) {
        eiendomsverdi.eiendom.eiendomId = ANNEN_EIENDOM_ID;
    }

    useEffect(() => {
        if (eiendomsverdi) {
            reset({
                harBolig: eiendomsverdi.harBolig,
                harETakst: eiendomsverdi.harEtakst,
                harBoligPant: eiendomsverdi.harPantIBolig,
                eTakst: eiendomsverdi.eiendom?.etakstWeb || undefined,
                eiendom: eiendomsverdi.eiendom || undefined,
                adresse:
                    eiendomsverdi.eiendom?.eiendomId === ANNEN_EIENDOM_ID ? eiendomsverdi.eiendom?.adresse : undefined,
                postnummer:
                    eiendomsverdi.eiendom?.eiendomId === ANNEN_EIENDOM_ID
                        ? eiendomsverdi?.eiendom?.postnummer
                        : undefined,
                poststed:
                    eiendomsverdi.eiendom?.eiendomId === ANNEN_EIENDOM_ID
                        ? eiendomsverdi?.eiendom?.poststed
                        : undefined,
                restSaldo: eiendomsverdi.eksisterendeLaanebeloep || undefined,
                oenskerVerdivurdering: eiendomsverdi.eiendom?.oenskerVerdivurdering || ValgtWeb.IKKE_VALGT,
                samtykke: eiendomsverdi.eiendom?.oenskerVerdivurdering === ValgtWeb.JA,
            });
        }
    }, [eiendomsverdi, reset]);

    const onSubmitBoligSalg = (data: BoligSalgFormData) => {
        const { eiendom, eTakst } = data;
        if (eiendomsverdi?.soeknadId) {
            if (eiendom) {
                eiendom.type = EiendomType.SELLING;
                eiendom.oenskerVerdivurdering = data.oenskerVerdivurdering;
                eiendom.etakstWeb = eTakst || null;
                if (eiendom.eiendomId === ANNEN_EIENDOM_ID) {
                    eiendom.adresse = data.adresse;
                    eiendom.postnummer = data.postnummer;
                    eiendom.poststed = data.poststed;
                }
            }
            updateEiendomverdi({
                soeknadId: eiendomsverdi.soeknadId,
                eiendom: eiendom || null,
                harEtakst: data.harETakst,
                harBolig: data.harBolig,
                harPantIBolig: data.harBoligPant,
                eksisterendeLaanebeloep: data.restSaldo || null,
                soektBeloep: null,
                nedbetalingstid: null,
                laaneformaal: [],
                annetLaaneformaal: null,
            });
        }
    };

    return (
        <div>
            <PageCard title={boligSalg.title}>
                <FormProvider {...methods}>
                    <form onSubmit={handleSubmit(onSubmitBoligSalg)}>
                        <div className="row row-gap-large">
                            {isLoadingEiendomsverdi && <Loader />}
                            {isFetchedEiendomsverdi && eiendomsverdi?.soeknadId && (
                                <BoligSalgValg soeknadId={eiendomsverdi.soeknadId} />
                            )}
                            {watchAll.harBolig === ValgtWeb.JA && (
                                <>
                                    <EiendomList />
                                    {watchAll.eiendom?.eiendomId === ANNEN_EIENDOM_ID && <AnnenEiendom />}
                                    {watchAll.eiendom?.eiendomId && eiendomsverdi?.soeknadId && (
                                        <>
                                            <ETakstValg />
                                            {watchAll.harETakst === ValgtWeb.JA && <ETakstSoek />}
                                            {watchAll.eiendom?.eiendomId !== ANNEN_EIENDOM_ID && (
                                                <BoligSalgInfoBox
                                                    soeknadId={eiendomsverdi?.soeknadId}
                                                    eksisterendeLaanebeloep={engasjement?.eksisterendeLaanebeloep}
                                                    isLoadingEngasjementer={isLoadingEngasjementer}
                                                />
                                            )}
                                            <BoligPant eksisterendeLaan={engasjement?.eksisterendeLaanebeloep} />
                                            {featureToggles.aktivsamarbeid && <Verdivurdering />}
                                        </>
                                    )}
                                </>
                            )}
                            <PageFooter
                                soeknadId={eiendomsverdi?.soeknadId}
                                onForrigeClick={() => navigate(navigateFrom)}
                                showSpinner={isUpdating}
                            />
                        </div>
                    </form>
                </FormProvider>
            </PageCard>
        </div>
    );
};

export default BoligSalg;
